import { PHASES } from '../../api/contrat.js';
import { presenterBlocage } from '../dossier/blocages.js';
import { MENTION_ESTIMATION, ancienneteJours, libelleValeur, trierDossiers, type DonneesPilotage, type LignePilotage } from './modele.js';

/**
 * « Exporter le point trimestriel » (D01) : un CSV construit à partir des données que l'écran a déjà (aucune route d'export au contrat).
 * UTF-8 avec BOM et séparateur « ; » pour Excel et Numbers en français. Le serveur ne sert pas le reste à encaisser par dossier : seul
 * le total figure, à la dernière ligne.
 */
export const BOM = '﻿';
export const MESSAGE_EXPORT = 'Point trimestriel exporté';
export const MESSAGE_ECHEC_EXPORT = 'Le point trimestriel n’a pas pu être exporté. Réessayez dans un instant.';

const ENTETE = ['Dossier', 'Référence', 'Phase', 'Statut', 'Statut métier', 'Blocage', 'Depuis (jours)', 'Estimation de départ', 'Reste à encaisser (€)'];
const STATUTS: Readonly<Record<LignePilotage['status'], string>> = {
  prospect: 'Prospect',
  actif: 'Actif',
  bloque: 'Bloqué',
  clos: 'Clos',
  abandonne: 'Abandonné',
};

export const nomFichierPointTrimestriel = (aujourdhui: string): string => `point-trimestriel-${aujourdhui}.csv`;

/** Une cellule : entre guillemets si elle contient « ; », un guillemet ou un saut de ligne ; une formule de tableur est neutralisée. */
function cellule(valeur: string | number | null): string {
  let t = valeur === null ? '' : String(valeur).replace(/[\u00a0\u202f]/g, ' ');
  if (/^[=+\-@\t\r]/.test(t) && !/^-?\d+(,\d+)?$/.test(t)) t = `'${t}`;
  return /[;"\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

/** Euros sans séparateur de milliers, virgule décimale seulement s'il y a des centimes (nombre lu comme tel par un tableur français). */
function eurosTableur(centimes: number): string {
  const entier = Math.trunc(centimes / 100);
  const reste = Math.abs(centimes % 100);
  if (reste === 0) return String(entier);
  return `${entier},${String(reste).padStart(2, '0')}`;
}

const libellePhase = (code: string): string => {
  const phase = PHASES.find((p) => p.code === code);
  return phase ? `${phase.code} · ${phase.libelle}` : code;
};

export function construireCsvPointTrimestriel(donnees: DonneesPilotage, aujourdhui: string): string {
  const { tableau } = donnees;
  const actifs = tableau.dossiers.filter((l) => l.status !== 'clos' && l.status !== 'abandonne');
  const lignes = trierDossiers(actifs).map((l) =>
    [
      l.title,
      l.reference,
      libellePhase(l.currentPhase),
      STATUTS[l.status],
      l.statutMetier?.libelle ?? '',
      l.blocker ? presenterBlocage(l.blocker).libelle : '',
      l.blocker ? ancienneteJours(l.blocker.since, aujourdhui) : '',
      l.estimatedValue ? `${libelleValeur(l)} (${MENTION_ESTIMATION})` : libelleValeur(l),
      '',
    ]
      .map(cellule)
      .join(';'),
  );
  const total = [`Total au ${aujourdhui}`, '', '', '', '', '', '', '', eurosTableur(tableau.outstanding.amountCents)].map(cellule).join(';');
  return `${BOM}${[ENTETE.join(';'), ...lignes, total].join('\r\n')}\r\n`;
}

/** Télécharge le contenu par le navigateur (lien temporaire sur un Blob), sans route ni stockage côté serveur. */
export function telechargerCsv(nom: string, contenu: string): void {
  const url = URL.createObjectURL(new Blob([contenu], { type: 'text/csv;charset=utf-8' }));
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = nom;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
